"use client";

import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaBox,
  FaUser,
  FaCreditCard,
  FaCalculator,
  FaDownload,
  FaPrint,
  FaCalendar,
  FaMoneyBillWave,
  FaUniversity,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import axios from "axios";

const FixedAssetsInvoice = () => {
  const [activeViewTab, setActiveViewTab] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [formData, setFormData] = useState({
    invoice_type: "sale",
    customer_id: "",
    customer_contact_id: "",
    title: "",
    reason: "",
    discount: 0,
    tax_id: "",
    items: [],
    extra_charges: [],
    payment_info: {
      method: "cash",
      asset_type: "cash",
      asset_id: "",
      amount: 0,
      immediate_payment: false,
      installments: [],
      // Payment method details
      cheque_number: "",
      cheque_date: "",
      bank_name: "",
      bank_branch: "",
      card_transaction_id: "",
      card_bank_name: "",
      card_holder_name: "",
      from_bank_details: "",
      to_bank_details: "",
      transaction_reference: "",
    },
  });

  // Fetch data based on active tab
  useEffect(() => {
    if (activeViewTab === "list") {
      fetchInvoices();
    } else if (activeViewTab === "create") {
      fetchBasicData();
      fetchFixedAssets();
    }
  }, [activeViewTab]);

  const fetchBasicData = async () => {
    try {
      const [customersRes, taxesRes, cashRes, bankRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/assets/customers`, {
          withCredentials: true,
        }),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/taxes`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank`,
          { withCredentials: true }
        ),
      ]);

      setCustomers(customersRes.data);
      setTaxes(taxesRes.data);
      setCashAccounts(cashRes.data);
      setBankAccounts(bankRes.data);
    } catch (err) {
      console.error("Failed to fetch basic data:", err);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError("");

    try {
      const endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/fixed-assets`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setInvoices(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchFixedAssets = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/fixed-assets/available-assets`,
        {
          params: { invoice_type: formData.invoice_type },
          withCredentials: true,
        }
      );
      setAssets(response.data);
    } catch (err) {
      console.error("Failed to fetch fixed assets:", err);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      fixed_assets_id: "",
      quantity: 1,
      rate: 0,
      description: "",
    };

    setFormData({
      ...formData,
      items: [...formData.items, newItem],
    });
  };

  const handleAddInstallment = () => {
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: [
          ...formData.payment_info.installments,
          {
            due_date: "",
            amount: 0,
            expected_method: "any",
          },
        ],
      },
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;

    // Auto-populate rate based on asset selection
    if (field.includes("_id") && value) {
      const asset = assets.find((a) => a.id.toString() === value.toString());
      if (asset) {
        // For fixed assets, we might not have a predefined rate
        // updatedItems[index].rate = asset.purchase_price || 0;
      }
    }

    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  const handleInstallmentChange = (index, field, value) => {
    const updatedInstallments = [...formData.payment_info.installments];
    updatedInstallments[index][field] = value;
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: updatedInstallments,
      },
    });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const removeInstallment = (index) => {
    const updatedInstallments = formData.payment_info.installments.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: updatedInstallments,
      },
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return (
        sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)
      );
    }, 0);
  };

  const calculateTaxAmount = () => {
    if (!formData.tax_id) return 0;
    const tax = taxes.find(
      (t) => t.id.toString() === formData.tax_id.toString()
    );
    if (!tax) return 0;

    const baseAmount = calculateSubtotal() - parseFloat(formData.discount || 0);
    return baseAmount * (parseFloat(tax.percentage) / 100);
  };

  const calculateTotalAmount = () => {
    return (
      calculateSubtotal() -
      parseFloat(formData.discount || 0) +
      calculateTaxAmount()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/fixed-assets`;

      const payload = {
        ...formData,
        items: formData.items.map((item) => ({
          ...item,
          quantity: parseInt(item.quantity) || 1,
          rate: parseFloat(item.rate) || 0,
        })),
        payment_info: formData.payment_info.immediate_payment
          ? {
              ...formData.payment_info,
              amount: parseFloat(formData.payment_info.amount) || 0,
            }
          : formData.payment_info,
      };

      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });

      if (response.status === 201) {
        setSuccess("Fixed Assets Invoice created successfully!");
        setShowCreateModal(false);
        resetForm();
        fetchInvoices();
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_type: "sale",
      customer_id: "",
      customer_contact_id: "",
      title: "",
      reason: "",
      discount: 0,
      tax_id: "",
      items: [],
      extra_charges: [],
      payment_info: {
        method: "cash",
        asset_type: "cash",
        asset_id: "",
        amount: 0,
        immediate_payment: false,
        installments: [],
        cheque_number: "",
        cheque_date: "",
        bank_name: "",
        bank_branch: "",
        card_transaction_id: "",
        card_bank_name: "",
        card_holder_name: "",
        from_bank_details: "",
        to_bank_details: "",
        transaction_reference: "",
      },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
    }).format(amount || 0);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "partially_paid":
        return "bg-yellow-100 text-yellow-800";
      case "issued":
        return "bg-blue-100 text-blue-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const renderItemForm = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <FaBox className="w-5 h-5 text-blue-600" />
            <span>Fixed Assets</span>
          </h4>
          <button
            type="button"
            onClick={handleAddItem}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 font-medium rounded-lg hover:bg-blue-200 transition-colors duration-200"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add Item</span>
          </button>
        </div>

        {formData.items.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-12 gap-4 p-4 bg-gray-50 rounded-lg"
          >
            <div className="col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset
              </label>
              <select
                value={item.fixed_assets_id || ""}
                onChange={(e) =>
                  handleItemChange(index, "fixed_assets_id", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Asset</option>
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qty
              </label>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  handleItemChange(index, "quantity", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate
              </label>
              <input
                type="number"
                value={item.rate}
                onChange={(e) =>
                  handleItemChange(index, "rate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total
              </label>
              <input
                type="number"
                value={
                  (parseFloat(item.quantity) || 0) *
                  (parseFloat(item.rate) || 0)
                }
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
            <div className="col-span-2 flex items-end">
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Remove item"
              >
                <FaTrash className="w-4 h-4" />
              </button>
            </div>
            <div className="col-span-12">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={item.description}
                onChange={(e) =>
                  handleItemChange(index, "description", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentForm = () => {
    const availableAccounts =
      formData.payment_info.asset_type === "cash" ? cashAccounts : bankAccounts;

    return (
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <FaCreditCard className="w-5 h-5 text-green-600" />
          <span>Payment Information</span>
        </h4>

        {/* Immediate Payment Toggle */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="immediate_payment"
            checked={formData.payment_info.immediate_payment}
            onChange={(e) =>
              setFormData({
                ...formData,
                payment_info: {
                  ...formData.payment_info,
                  immediate_payment: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="immediate_payment"
            className="text-sm font-medium text-gray-700"
          >
            Process immediate payment
          </label>
        </div>

        {/* Payment Details (if immediate payment is selected) */}
        {formData.payment_info.immediate_payment && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                value={formData.payment_info.method}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      method: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Type
              </label>
              <select
                value={formData.payment_info.asset_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      asset_type: e.target.value,
                      asset_id: "",
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cash">Cash Account</option>
                <option value="bank">Bank Account</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account
              </label>
              <select
                value={formData.payment_info.asset_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      asset_id: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Account</option>
                {availableAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name || account.bank_name} (
                    {formatCurrency(account.amount)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount
              </label>
              <input
                type="number"
                value={formData.payment_info.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      amount: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
                step="0.01"
                placeholder={`Max: ${formatCurrency(calculateTotalAmount())}`}
              />
            </div>

            {/* Payment Method Specific Fields */}
            {formData.payment_info.method === "cheque" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.cheque_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          cheque_number: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cheque Date
                  </label>
                  <input
                    type="date"
                    value={formData.payment_info.cheque_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          cheque_date: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.bank_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          bank_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Branch
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.bank_branch}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          bank_branch: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {formData.payment_info.method === "card" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_transaction_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_transaction_id: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Bank
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_bank_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_bank_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Holder Name
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_holder_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_holder_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {formData.payment_info.method === "bank_transfer" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Bank Details
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.from_bank_details}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          from_bank_details: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Bank Details
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.to_bank_details}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          to_bank_details: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.transaction_reference}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          transaction_reference: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Installments Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-semibold text-gray-900 flex items-center space-x-2">
              <FaCalendar className="w-4 h-4 text-purple-600" />
              <span>Payment Installments (Optional)</span>
            </h5>
            <button
              type="button"
              onClick={handleAddInstallment}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 font-medium rounded-lg hover:bg-purple-200 transition-colors duration-200"
            >
              <FaPlus className="w-3 h-3" />
              <span>Add Installment</span>
            </button>
          </div>

          {formData.payment_info.installments.map((installment, index) => (
            <div
              key={index}
              className="grid grid-cols-12 gap-4 p-3 bg-purple-50 rounded-lg"
            >
              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={installment.due_date}
                  onChange={(e) =>
                    handleInstallmentChange(index, "due_date", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  value={installment.amount}
                  onChange={(e) =>
                    handleInstallmentChange(index, "amount", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Method
                </label>
                <select
                  value={installment.expected_method}
                  onChange={(e) =>
                    handleInstallmentChange(
                      index,
                      "expected_method",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="any">Any Method</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div className="col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={() => removeInstallment(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Remove installment"
                >
                  <FaTrash className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              Fixed Assets Invoice
            </h2>
            <p className="mt-2 text-gray-600">
              Create and manage invoices for fixed assets
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setActiveViewTab("list")}
              className={`px-6 py-3 font-medium rounded-xl transition-all duration-200 ${
                activeViewTab === "list"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-700 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              View Invoices
            </button>
            <button
              onClick={() => {
                setActiveViewTab("create");
                setShowCreateModal(true);
              }}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Invoice</span>
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaCheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Success!</span>
            <span className="text-green-700">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaTimes className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">Error!</span>
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {activeViewTab === "list" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {invoices.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaFileInvoiceDollar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No invoices found
              </h3>
              <p className="text-gray-500 mb-6">
                Get started by creating your first fixed assets invoice
              </p>
              <button
                onClick={() => {
                  setActiveViewTab("create");
                  setShowCreateModal(true);
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
              >
                <FaPlus className="w-4 h-4" />
                <span>Create Your First Invoice</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{invoice.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            invoice.invoice_type === "sale"
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {invoice.invoice_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.customer_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                            invoice.status
                          )}`}
                        >
                          {invoice.status.replace("_", " ").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            /* viewInvoice(invoice.id) */
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                          title="View invoice"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Create Fixed Assets Invoice
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-8">
              {/* Basic Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Type *
                  </label>
                  <select
                    value={formData.invoice_type}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        invoice_type: e.target.value,
                      });
                      fetchFixedAssets();
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="sale">Sale</option>
                    <option value="purchase">Purchase</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) =>
                      setFormData({ ...formData, customer_id: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select Customer (Optional)</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} ({customer.type})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Invoice title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax
                  </label>
                  <select
                    value={formData.tax_id}
                    onChange={(e) =>
                      setFormData({ ...formData, tax_id: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">No Tax</option>
                    {taxes.map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.name} ({tax.percentage}%)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    rows="3"
                    placeholder="Invoice description or reason"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount
                  </label>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Items */}
              {renderItemForm()}

              {/* Payment Information */}
              {renderPaymentForm()}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <FaCalculator className="w-5 h-5 text-blue-600" />
                  <span>Invoice Summary</span>
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Subtotal</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculateSubtotal())}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Discount</p>
                    <p className="text-lg font-semibold text-red-600">
                      -{formatCurrency(formData.discount)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Tax</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculateTaxAmount())}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatCurrency(calculateTotalAmount())}
                    </p>
                  </div>
                </div>

                {/* Payment Summary */}
                {(formData.payment_info.immediate_payment ||
                  formData.payment_info.installments.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {formData.payment_info.immediate_payment && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            Immediate Payment
                          </p>
                          <p className="text-lg font-semibold text-green-600">
                            {formatCurrency(formData.payment_info.amount)}
                          </p>
                        </div>
                      )}
                      {formData.payment_info.installments.length > 0 && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            Installments Total
                          </p>
                          <p className="text-lg font-semibold text-purple-600">
                            {formatCurrency(
                              formData.payment_info.installments.reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              )
                            )}
                          </p>
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-sm text-gray-600">
                          Remaining Balance
                        </p>
                        <p
                          className={`text-lg font-semibold ${
                            calculateTotalAmount() -
                              (parseFloat(formData.payment_info.amount) || 0) -
                              formData.payment_info.installments.reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              ) >
                            0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {formatCurrency(
                            calculateTotalAmount() -
                              (parseFloat(formData.payment_info.amount) || 0) -
                              formData.payment_info.installments.reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              )
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-6 py-3 text-gray-700 bg-gray-100 font-medium rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || formData.items.length === 0}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <FaSave className="w-4 h-4" />
                  )}
                  <span>{loading ? "Creating..." : "Create Invoice"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedAssetsInvoice;
